// ===========================================
// Profile Section Component
// ===========================================
// Displays profile information with integrated avatar.
// Non-student users see their avatar with an edit badge;
// students see the default Person icon (no avatar system).
// ===========================================

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import type { ProfileFormData, UserProfile } from './account.types';
import { CARD_COLORS } from './account.constants';
import { AvatarDisplay } from '../../avatar';
import { useAvatarManagement } from '../../../hooks/useAvatar';
import { validateAvatarFileAsync, DEFAULT_AVATAR_SETTINGS } from '../../../types/avatar.types';
import ConfirmDialog from '../../common/ConfirmDialog';

interface Props {
  profile: UserProfile | null;
  isEditing: boolean;
  formData: ProfileFormData;
  isFunMode: boolean;
  isUpdating: boolean;
  isStudent: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onFormChange: (data: ProfileFormData) => void;
}

export default function ProfileSection({
  profile,
  isEditing,
  formData,
  isFunMode,
  isUpdating,
  isStudent,
  onStartEdit,
  onCancelEdit,
  onSave,
  onFormChange,
}: Props) {
  // Avatar state (non-students only)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const {
    avatar,
    canUpload,
    isUploading,
    isRemoving,
    uploadError,
    upload,
    remove,
  } = useAvatarManagement();

  const settings = DEFAULT_AVATAR_SETTINGS;
  const uploadDisabled = isStudent || !canUpload || isUploading || isRemoving || isValidating;

  // Clear validation error when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      setValidationError(null);
    }
  }, [isEditing]);

  // File selection: validate then upload immediately
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsValidating(true);
      setValidationError(null);

      try {
        const validation = await validateAvatarFileAsync(file, settings);
        if (!validation.valid) {
          setValidationError(validation.error || 'Invalid file');
          setIsValidating(false);
          return;
        }

        setIsValidating(false);
        await upload(file);
      } catch {
        setValidationError('Failed to upload photo');
        setIsValidating(false);
      } finally {
        // Reset file input so the same file can be re-selected
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [settings, upload]
  );

  const handleRemove = useCallback(async () => {
    try {
      await remove();
      setShowRemoveDialog(false);
    } catch {
      // Error handled by hook
    }
  }, [remove]);

  const handleAvatarClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Render the avatar area (non-students) or Person icon (students)
  const renderIcon = () => {
    if (isStudent) {
      return (
        <Box
          sx={{
            bgcolor: isFunMode ? `${CARD_COLORS.profile.border}30` : 'primary.light',
            borderRadius: '50%',
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isFunMode ? CARD_COLORS.profile.border : 'primary.main',
            flexShrink: 0,
          }}
        >
          <PersonIcon />
        </Box>
      );
    }

    return (
      <Box sx={{ flexShrink: 0 }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={settings.allowedFormats.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={uploadDisabled}
        />

        {/* Avatar with camera badge */}
        <Box sx={{ position: 'relative', width: 64, height: 64 }}>
          <AvatarDisplay
            avatarInfo={avatar}
            firstName={profile?.firstName}
            lastName={profile?.lastName}
            role={profile?.role as 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT'}
            size={64}
          />

          {/* Camera edit badge (only in edit mode) */}
          {isEditing && canUpload && (
            <Tooltip title="Change photo">
              <IconButton
                onClick={handleAvatarClick}
                disabled={uploadDisabled}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 24,
                  height: 24,
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Loading overlay */}
          {(isUploading || isRemoving || isValidating) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '50%',
              }}
            >
              <CircularProgress size={24} sx={{ color: 'white' }} />
            </Box>
          )}
        </Box>

        {/* Remove button (only in edit mode, if has avatar) */}
        {isEditing && avatar?.avatarUrl && canUpload && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
            <Tooltip title="Remove photo">
              <IconButton
                onClick={() => setShowRemoveDialog(true)}
                disabled={isUploading || isRemoving}
                size="small"
                color="error"
                sx={{ width: 24, height: 24 }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Validation error */}
        {validationError && (
          <Alert
            severity="error"
            onClose={() => setValidationError(null)}
            sx={{ mt: 1, maxWidth: 200, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
          >
            {validationError}
          </Alert>
        )}

        {/* Upload error */}
        {uploadError && (
          <Alert
            severity="error"
            sx={{ mt: 1, maxWidth: 200, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
          >
            {uploadError instanceof Error ? uploadError.message : 'Upload failed'}
          </Alert>
        )}

        {/* Remove confirmation dialog */}
        <ConfirmDialog
          open={showRemoveDialog}
          title="Remove Avatar"
          message="Are you sure you want to remove your avatar? Your initials will be displayed instead."
          confirmLabel="Remove"
          confirmColor="error"
          loading={isRemoving}
          onConfirm={handleRemove}
          onCancel={() => setShowRemoveDialog(false)}
        />
      </Box>
    );
  };

  return (
    <Card
      sx={{
        mb: 3,
        ...(isFunMode && {
          borderRadius: 4,
          border: `3px solid ${CARD_COLORS.profile.border}`,
          bgcolor: CARD_COLORS.profile.bg,
          boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
        }),
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {renderIcon()}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">Profile Information</Typography>
              {!isEditing && (
                <Button startIcon={<EditIcon />} onClick={onStartEdit} size="small">
                  Edit
                </Button>
              )}
            </Box>

            {isEditing ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.firstName || profile?.firstName || ''}
                    onChange={(e) =>
                      onFormChange({ ...formData, firstName: e.target.value })
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.lastName || profile?.lastName || ''}
                    onChange={(e) =>
                      onFormChange({ ...formData, lastName: e.target.value })
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone || profile?.phone || ''}
                    onChange={(e) => onFormChange({ ...formData, phone: e.target.value })}
                    size="small"
                    placeholder="+61 400 000 000"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile?.email || ''}
                    disabled
                    size="small"
                    helperText="Contact support to change email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={isUpdating ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={onSave}
                      disabled={isUpdating}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={onCancelEdit}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">
                    {profile?.firstName} {profile?.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{profile?.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{profile?.phone || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Chip label={profile?.role} size="small" color="primary" />
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
